import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AuthEntryCard } from '@/features/auth/components/AuthEntryCard'
import { Button } from '@/shared/components/ui/Button'
import { passwordResetService } from '@/features/auth/services/passwordReset.service'
import { getPasswordResetState } from '@/features/auth/store/passwordReset.store'

const VALID_SYMBOLS = /[-@!"#$*%&/=?]/

function getStrengthColor(completedRules: number) {
  if (completedRules >= 4) return 'success'
  if (completedRules >= 2) return 'warning'
  return 'danger'
}

export function ForgotPasswordNewPasswordPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('auth')

  const { correlationId } = getPasswordResetState()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [samePasswordError, setSamePasswordError] = useState('')

  useEffect(() => {
    if (!correlationId) {
      navigate('/forgot-password', { replace: true })
    }
  }, [correlationId, navigate])

  const passwordRules = useMemo(
    () => [
      {
        id: 'length',
        label: t('auth:forgotPassword.password.rule.length'),
        test: (value: string) => value.length >= 8 && value.length <= 16,
      },
      {
        id: 'number',
        label: t('auth:forgotPassword.password.rule.number'),
        test: (value: string) => /\d/.test(value),
      },
      {
        id: 'symbol',
        label: t('auth:forgotPassword.password.rule.symbol'),
        test: (value: string) => VALID_SYMBOLS.test(value),
      },
      {
        id: 'uppercase',
        label: t('auth:forgotPassword.password.rule.uppercase'),
        test: (value: string) => /[A-Z]/.test(value),
      },
    ],
    [t],
  )

  const ruleResults = useMemo(
    () => passwordRules.map((rule) => ({ ...rule, passed: rule.test(password) })),
    [password, passwordRules],
  )

  const completedRules = ruleResults.filter((rule) => rule.passed).length
  const strengthState = getStrengthColor(completedRules)
  const passwordsMatch = password.length > 0 && password === confirmPassword
  const passwordsMismatch = confirmPassword.length > 0 && !passwordsMatch
  const canContinue =
    completedRules === passwordRules.length && passwordsMatch && !isSubmitting

  async function handleSave() {
    if (!canContinue || !correlationId) return

    setIsSubmitting(true)

    const result = await passwordResetService.complete(correlationId, {
      newPassword: password,
      newPasswordConfirmation: confirmPassword,
    })

    setIsSubmitting(false)

    if (result.success) {
      navigate('/forgot-password/success')
      return
    }

    if (result.errorCode === 'ERR_PASSWORD_RESET_SAME_PASSWORD') {
      setSamePasswordError(t('auth:forgotPassword.password.error.samePassword'))
      setPassword('')
      setConfirmPassword('')
      return
    }

    if (result.errorCode === 'ERR_PASSWORD_RESET_PREVIOUSLY_USED') {
      setSamePasswordError(t('auth:forgotPassword.password.error.previouslyUsed'))
      setPassword('')
      setConfirmPassword('')
      return
    }

    setErrorMessage(t('auth:forgotPassword.password.error.description'))
    setShowErrorModal(true)
  }

  return (
    <>
      <AuthEntryCard variant="password">
        <section className="mt-4 w-full max-w-96 text-center">
          <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">
            {t('auth:forgotPassword.password.title')}
          </h1>
          <p className="mt-3 text-sm leading-6 text-text-muted">
            {t('auth:forgotPassword.password.description')}
          </p>
        </section>

        <div className="mt-8 flex w-full max-w-96 flex-col gap-5">
          <label className="flex flex-col gap-2.5">
            <span className="text-[0.8125rem] leading-6 text-text-muted">
              {t('auth:forgotPassword.password.field.label')}
            </span>
            <span className="relative flex items-center">
              <input
                className="min-h-[2.125rem] w-full rounded border border-border-soft bg-surface-card px-3 py-[0.4375rem] pr-9 text-[0.8125rem] leading-6 text-text-primary transition-[border-color,box-shadow] duration-150 placeholder:text-[#99a7b5] focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
                maxLength={16}
                onChange={(event) => {
                  setPassword(event.target.value)
                  setSamePasswordError('')
                }}
                placeholder={t('auth:forgotPassword.password.field.placeholder')}
                type={showPassword ? 'text' : 'password'}
                value={password}
              />
              <button
                aria-label={
                  showPassword
                    ? t('auth:forgotPassword.password.hide')
                    : t('auth:forgotPassword.password.show')
                }
                className="absolute right-4 inline-flex items-center border-none bg-transparent p-0 text-[#606975] cursor-pointer focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                onClick={() => setShowPassword((v) => !v)}
                type="button"
              >
                ◉
              </button>
            </span>
          </label>

          <div className="h-2 w-full overflow-hidden rounded-full bg-[#e5edf4]">
            <span
              className={[
                'block h-full rounded-full transition-[width,background-color] duration-200',
                strengthState === 'success'
                  ? 'bg-[var(--color-success,#22c55e)]'
                  : strengthState === 'warning'
                    ? 'bg-[var(--color-warning,#f59e0b)]'
                    : 'bg-[#ff384d]',
              ].join(' ')}
              data-strength={strengthState}
              style={{
                width: `${Math.max((completedRules / passwordRules.length) * 100, 4)}%`,
              }}
            />
          </div>

          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {ruleResults.map((rule) => (
              <li key={rule.id} className="flex items-center gap-2 text-sm leading-5 text-text-muted" data-passed={rule.passed}>
                <span className={rule.passed ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#e8f8ef] text-xs text-[var(--color-success,#22c55e)]' : 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#eef3f7] text-xs text-text-muted'} aria-hidden="true">
                  {rule.passed ? '✓' : '○'}
                </span>
                <span>{rule.label}</span>
              </li>
            ))}
          </ul>

          <label className="flex flex-col gap-2.5">
            <span className="text-[0.8125rem] leading-6 text-text-muted">
              {t('auth:forgotPassword.password.confirm.label')}
            </span>
            <span className="relative flex items-center">
              <input
                className="min-h-[2.125rem] w-full rounded border border-border-soft bg-surface-card px-3 py-[0.4375rem] pr-9 text-[0.8125rem] leading-6 text-text-primary transition-[border-color,box-shadow] duration-150 placeholder:text-[#99a7b5] focus-visible:border-brand-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
                maxLength={16}
                onChange={(event) => setConfirmPassword(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') void handleSave() }}
                placeholder={t('auth:forgotPassword.password.field.placeholder')}
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
              />
              <button
                aria-label={
                  showConfirmPassword
                    ? t('auth:forgotPassword.password.hideConfirm')
                    : t('auth:forgotPassword.password.showConfirm')
                }
                className="absolute right-4 inline-flex items-center border-none bg-transparent p-0 text-[#606975] cursor-pointer focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                onClick={() => setShowConfirmPassword((v) => !v)}
                type="button"
              >
                ◉
              </button>
            </span>
          </label>

          {samePasswordError ? (
            <p className="text-center text-sm leading-6 text-[#e02d3c]" role="alert">
              {samePasswordError}
            </p>
          ) : passwordsMatch ? (
            <p className="text-center text-sm leading-6 text-[var(--color-success,#22c55e)]">
              {t('auth:forgotPassword.password.match')}
            </p>
          ) : passwordsMismatch ? (
            <p className="text-center text-sm leading-6 text-[#e02d3c]" role="alert">
              {t('auth:forgotPassword.password.mismatch')}
            </p>
          ) : null}
        </div>

        <div className="mt-8 flex w-full max-w-96 justify-center">
          <Button className="min-h-9 px-5 py-1.5 text-sm disabled:bg-[#f1f1f3] disabled:text-[#8c939b] disabled:opacity-100" disabled={!canContinue} onClick={() => void handleSave()}>
            {isSubmitting
              ? t('auth:forgotPassword.password.btn.saving')
              : t('auth:forgotPassword.password.btn.save')}
          </Button>
        </div>
      </AuthEntryCard>

      {showErrorModal ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ffebee] text-2xl font-bold text-[#ff384d]"
              aria-hidden="true"
            >
              !
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">
              {t('auth:forgotPassword.password.error.title')}
            </h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {errorMessage}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 bg-[#ff384d] px-5 py-1.5 text-sm hover:bg-[#e03245] active:bg-[#c92d3e]"
                onClick={() => setShowErrorModal(false)}
              >
                {t('auth:forgotPassword.password.error.btn')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
